import * as React from 'react';
import {useSelector} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';
import type {SavedFilter} from 'nutshell-core/types';

import {ShareButton} from 'shells/button/share-button/share-button';

import type {GetAudienceForEntityList_audience as Audience} from 'nutshell-graphql-types';

import {TitleBarShareButtonUpsell} from '../../base/title-bar/title-bar-share-button-upsell';

import type {ShareType} from '../share-selection';

import {FilterShareModalWrapper} from './filter-share-modal-wrapper';

type Props = {
    savedFilter: SavedFilter<any> | null | undefined;
    audience?: Audience | null | undefined;
};

export function FilterShareButton(props: Props) {
    const [isOpen, setIsOpen] = React.useState(false);
    const assignables = useSelector(Assignables.getAllEnabled);
    const shareType =
        props.audience && !props.savedFilter
            ? 'everyone'
            : getShareTypeFromFilter(props.savedFilter);

    return (
        <>
            {NutClientConfig.hasSharableListsAndReports ? (
                <ShareButton
                    shareType={shareType}
                    // @ts-expect-error
                    onClick={() => setIsOpen(true)}
                    // @ts-expect-error
                    tooltipText={getShareTooltip(assignables, props.savedFilter, props.audience)}
                    size='big'
                    disabled={
                        !props.savedFilter || !props.savedFilter.isUpdateable || props.audience
                    }
                />
            ) : (
                <TitleBarShareButtonUpsell parentType='entity-list' />
            )}
            {isOpen && props.savedFilter && (
                <FilterShareModalWrapper
                    title='Manage sharing'
                    shareType={shareType}
                    savedFilter={props.savedFilter}
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    // @ts-expect-error
                    assignables={assignables}
                />
            )}
        </>
    );
}

export function getShareTypeFromFilter(savedFilter?: SavedFilter<any> | null): ShareType {
    if (!savedFilter || (!savedFilter.isShared && savedFilter.createdTime)) return 'nobody';

    return savedFilter.links.usersAndTeams && savedFilter.links.usersAndTeams.length
        ? 'individuals'
        : 'everyone';
}

function getShareTooltip(
    usersAndTeams: any[],
    activeSavedFilter?: SavedFilter<any> | null,
    audience?: Audience | null
): string {
    if (audience) {
        return 'Shared with everyone';
    }

    if (!activeSavedFilter || (!activeSavedFilter.isShared && activeSavedFilter.createdTime)) {
        return 'Private to only me';
    }

    const sharedWithCount =
        activeSavedFilter.links.usersAndTeams && activeSavedFilter.links.usersAndTeams.length;

    if (activeSavedFilter.isShared && sharedWithCount) {
        const firstEntityId = activeSavedFilter.links.usersAndTeams[0];
        const firstEntity = usersAndTeams.find((userOrTeam) => {
            return userOrTeam.id === firstEntityId;
        });

        if (!firstEntity) {
            return 'Sharing with individuals on your team';
        }

        if (sharedWithCount === 1) {
            return `Shared with ${firstEntity.name}`;
        } else if (sharedWithCount === 2) {
            return `Shared with ${firstEntity.name} and ${sharedWithCount - 1} other`;
        } else {
            return `Shared with ${firstEntity.name} and ${sharedWithCount - 1} others`;
        }
    }

    return 'Shared with everyone';
}
